/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';

import { ConfirmDeleteDialog, MemberNotificationItem } from 'components';
import { useDeleteMemberNotificationMutation, useGetMemberNotificationsQuery } from 'store/api/version-api.slice';

import AddNotificationEmailDialog from './AddNotificationEmailDialog';
import EditNotificationEmailDialog from './EditNotificationEmailDialog';
import { styles } from './styles';

const MemberNotifications = () => {
	const [deleteId, setDeleteId] = useState(null);
	const [dialogs, setDialogs] = useState({
		edit: false,
		add: false,
		delete: false
	});

	const [editEmailForm, setEditEmailForm] = useState({
		email_type: '',
		email_subject: '',
		frequency: 90,
		email_body: '',
		memberemailformcc_set: []
	});

	const { data: memberNotificationsResult = {} } = useGetMemberNotificationsQuery();
	const memberNotifications = memberNotificationsResult.results || [];

	const [deleteMemberNotification, { isLoading: isDeleteLoading }] = useDeleteMemberNotificationMutation();

	const handleAddDialogOpen = () => {
		setEditEmailForm({
			email_type: '',
			email_subject: '',
			frequency: 90,
			email_body: '',
			memberemailformcc_set: []
		});

		setDialogs({
			...dialogs,
			add: true
		});
	};

	const handleEditDialogOpen = email => {
		setEditEmailForm(email);
		setDialogs({
			...dialogs,
			edit: true
		});
	};

	const handleDeleteDialogOpen = item => {
		const { id } = item;
		setDeleteId(id);
		setDialogs({
			...dialogs,
			delete: true
		});
	};

	const handleDialogClose = () => {
		setDeleteId(null);
		setDialogs({
			edit: false,
			add: false
		});
	};

	const handleEditFormValueChange = useCallback((prop, value) => {
		setEditEmailForm(prev => ({
			...prev,
			[prop]: value
		}));
	}, []);

	const handleDeleteEmail = async () => {
		await deleteMemberNotification(deleteId);

		handleDialogClose();
	};

	return (
		<Box sx={styles.root}>
			<Box>
				<Paper elevation={2}>
					<Toolbar>
						<Typography component={'span'} variant="h6" color="primary" sx={styles.title}>
							MEMBER NOTIFICATIONS
						</Typography>
						<Fab size="small" color="primary" onClick={handleAddDialogOpen}>
							<AddIcon />
						</Fab>
					</Toolbar>
					<Divider />
					<List>
						{memberNotifications.map((item, index) => (
							<MemberNotificationItem
								key={`member-notifications-${index}`}
								item={item}
								onEdit={handleEditDialogOpen}
								onDelete={handleDeleteDialogOpen}
							/>
						))}
					</List>
				</Paper>
			</Box>
			<EditNotificationEmailDialog
				dialogs={dialogs}
				editEmailForm={editEmailForm}
				handleDialogClose={handleDialogClose}
				handleEditFormValueChange={handleEditFormValueChange}
			/>
			<AddNotificationEmailDialog dialogs={dialogs} handleDialogClose={handleDialogClose} />
			<ConfirmDeleteDialog
				open={dialogs.delete}
				handleDeleteItem={handleDeleteEmail}
				onClose={handleDialogClose}
				content={'This action is ireversable. Are you sure you wish to delete this member notification email?'}
				title={'Delete Member Notification Email'}
				disabled={isDeleteLoading}
				isLoading={isDeleteLoading}
			/>
		</Box>
	);
};

export default MemberNotifications;
